/**
 * Feedback Service
 *
 * Data access for the two independent feedback channels:
 * parent dashboard feedback and kids mode feedback.
 */

import { apiGet, apiPost } from '@/lib/api';

const CONTEXT = 'feedback';

export type FeedbackSource = 'parent' | 'kids';

export interface Feedback {
  id: string;
  user_id: string;
  child_profile_id: string | null;
  source: FeedbackSource;
  rating: number | null;
  message: string;
  page: string | null;
  created_at: string;
}

/** Feedback row enriched with joined fields, as returned by the admin list. */
export interface AdminFeedback extends Feedback {
  user_email: string;
  child_name: string | null;
}

export interface SubmitFeedbackInput {
  message: string;
  rating?: number | null;
  page?: string | null;
}

/**
 * Submit feedback from the parent dashboard.
 */
export async function submitParentFeedback(data: SubmitFeedbackInput): Promise<Feedback> {
  return apiPost<Feedback>('/feedback', `${CONTEXT}.submitParentFeedback`, data);
}

/**
 * Submit feedback from kids mode for a specific child.
 */
export async function submitKidsFeedback(childId: string, data: SubmitFeedbackInput): Promise<Feedback> {
  return apiPost<Feedback>(`/feedback/kids/${childId}`, `${CONTEXT}.submitKidsFeedback`, data);
}

/**
 * Admin-only: fetch all feedback across every user.
 */
export async function getAllFeedback(): Promise<AdminFeedback[]> {
  return apiGet<AdminFeedback[]>('/feedback', `${CONTEXT}.getAllFeedback`, { defaultValue: [] });
}

export const feedbackService = {
  submitParentFeedback,
  submitKidsFeedback,
  getAllFeedback,
};
